import { createContext, PropsWithChildren, useContext, useState } from 'react';
import {
  Course,
  GradingRequest,
  MemberEnrollment,
  Membership,
  Organization,
} from '../../../../App.interfaces';
import { IHighlightedSection } from '../HighlightText/HighlightText';

interface OrganizationContextType {
  selectedOrganization: Organization | undefined;
  setSelectedOrganization: (organization: Organization) => void;
  selectedDepartment: Organization | undefined;
  setSelectedDepartment: (department: Organization | undefined) => void;
  membershipsView: {
    selectedMembership: Membership | null;
    setSelectedMembership: (membership: Membership | null) => void;
  };
  coursesView: {
    course: Course | null;
    setCourse: (course: Course | null) => void;
    selectedEnrollment: MemberEnrollment | null;
    setSelectedEnrollment: (enrollment: MemberEnrollment | null) => void;
  };
  crqView: {
    selectedGradingRequest: GradingRequest | null;
    setSelectedGradingRequest: (request: GradingRequest | null) => void;
    highlightedSection: string[];
    setHighlightedSection: (section: string[]) => void;
    overallComment: string;
    setOverallComment: (comment: string) => void;
    allCommentsHighlightedSection: IHighlightedSection[];
    setAllCommentsHighlightedSection: (sections: IHighlightedSection[]) => void;
  };
  orgMembership: Membership | undefined;
  setOrgMembership: (membership: Membership | undefined) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

export const useOrganizationContext = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      'useOrganizationTabContext must be used within a MonitorTabProvider',
    );
  }
  return context;
};

export const OrganizationProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [orgMembership, setOrgMembership] = useState<Membership>();
  const [selectedMembership, setSelectedMembership] =
    useState<Membership | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] =
    useState<MemberEnrollment | null>(null);
  const [selectedGradingRequest, setSelectedGradingRequest] =
    useState<GradingRequest | null>(null);
  const [highlightedSection, setHighlightedSection] = useState<string[]>([]);
  const [overallComment, setOverallComment] = useState<string>('');
  const [allCommentsHighlightedSection, setAllCommentsHighlightedSection] =
    useState<IHighlightedSection[]>([]);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>();
  const [selectedDepartment, setSelectedDepartment] = useState<Organization>();

  return (
    <OrganizationContext.Provider
      value={{
        selectedOrganization,
        setSelectedOrganization,
        selectedDepartment,
        setSelectedDepartment,
        orgMembership,
        setOrgMembership,
        membershipsView: { selectedMembership, setSelectedMembership },
        coursesView: {
          course,
          setCourse,
          selectedEnrollment,
          setSelectedEnrollment,
        },
        crqView: {
          selectedGradingRequest,
          setSelectedGradingRequest,
          highlightedSection,
          setHighlightedSection,
          overallComment,
          setOverallComment,
          allCommentsHighlightedSection,
          setAllCommentsHighlightedSection,
        },
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
